/**
 * iOS Safari · Add-to-Home-Screen Banner · v2.7.65+E88
 * ═══════════════════════════════════════════════════════════════════
 * Renders a one-time gentle nudge for drivers who open the app in
 * regular Safari (macOS or iOS) — not in a Home-Screen PWA. Explains
 * that adding the app to the Home Screen unlocks background push
 * notifications; without it, dispatch falls back to SMS.
 *
 * Dismissal is remembered on the device (`localStorage.rtg_a2hs_dismissed`),
 * so once the driver has acknowledged it either by installing or by
 * hitting the "Later" button, they won't see it again on that device.
 * The banner ALSO auto-hides once the app detects it's running as a PWA.
 */
import { useEffect, useState } from "react";
import { X, Share, Plus } from "lucide-react";
import { detectBrowser } from "@/lib/driverPush";

const KEY = "rtg_a2hs_dismissed";

export default function DriverA2HSBanner() {
  const [visible, setVisible] = useState(false);
  const [meta, setMeta] = useState({ family: "unknown", isPWA: false });

  useEffect(() => {
    const m = detectBrowser();
    setMeta(m);
    const dismissed = (() => {
      try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
    })();
    // Only show for Safari-that's-not-a-PWA. Everyone else already has
    // native push (Chrome/FF/Edge) or is unsupported (very old iOS).
    if (m.family === "safari" && !m.isPWA && !dismissed) {
      // Slight delay so the banner doesn't nail-hammer the load flow.
      const t = setTimeout(() => setVisible(true), 1600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(KEY, "1"); } catch { /* private mode */ }
    setVisible(false);
  };

  return (
    <div
      data-testid="driver-a2hs-banner"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9500] max-w-md w-[92%]
                 rounded-2xl border border-amber-500/50 bg-zinc-950/95 shadow-2xl shadow-amber-500/20 backdrop-blur"
    >
      <div className="p-4 pb-3">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-xl bg-amber-500/10 border border-amber-500/40 shrink-0">
            <Plus className="h-5 w-5 text-amber-300"/>
          </div>
          <div className="flex-1">
            <div className="font-black text-sm text-zinc-100">Add Buller to your Home Screen</div>
            <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">
              Safari only sends background alerts when the app lives on your Home Screen.
              Install once — dispatch pings will buzz your phone even when the tab is closed.
            </p>
            <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500 flex-wrap">
              <span className="inline-flex items-center gap-1">Tap <Share className="h-3 w-3 text-sky-400"/> Share</span>
              <span>→</span>
              <span className="inline-flex items-center gap-1">Add to Home Screen</span>
              <span>→</span>
              <span>Add</span>
            </div>
          </div>
          <button
            data-testid="driver-a2hs-close"
            onClick={dismiss}
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4"/>
          </button>
        </div>
      </div>
      <div className="border-t border-zinc-800 flex">
        <button
          data-testid="driver-a2hs-later"
          onClick={dismiss}
          className="flex-1 py-2.5 text-[11px] text-zinc-400 hover:bg-zinc-900 transition-colors"
        >
          Later
        </button>
        <button
          data-testid="driver-a2hs-installed"
          onClick={dismiss}
          className="flex-1 py-2.5 text-[11px] text-amber-300 font-bold hover:bg-amber-500/5 transition-colors"
        >
          I've installed it
        </button>
      </div>
    </div>
  );
}
